import React, { useRef, useState } from 'react';
import { Arrow, useHover, useLayer } from 'react-laag';
import { MCText } from './MCText';
import { remCss, rgba, scssVariables, isReactText, lang } from 'src/helpers';
import SVGIcon, { BASE_ICONS } from '../svg';

export const EllipsisTooltip = ({ children, text, className = '', onlyEllipsis = false }) => {
  const textRef = useRef(null);
  const [isOver, hoverProps] = useHover({ delayEnter: 100, delayLeave: 300 });
  const [isOpen, setIsOpen] = useState(!onlyEllipsis);

  const { renderLayer, triggerProps, layerProps, arrowProps } = useLayer({
    isOpen: isOver && isOpen,
    placement: 'bottom-start',
    triggerOffset: 10
  });

  const handleMouseEnter = () => {
    const element = textRef.current;
    if (element) {
      if (element.scrollWidth > element.clientWidth) {
        setIsOpen(true);
      }
    }
  };

  return (
    <>
      <span
        className={className}
        {...triggerProps}
        {...hoverProps}
        onMouseEnter={(e) => {
          hoverProps.onMouseEnter(e);
          handleMouseEnter(e);
        }}
      >
        {children}
      </span>
      <div
        ref={textRef}
        style={{
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          lineHeight: 0,
          height: 0
        }}
      >
        {children}
      </div>
      {renderLayer(
        <div
          {...layerProps}
          style={{ ...layerProps.style }}
          className={'tooltip ' + (isOver && isOpen ? 'show' : 'hide')}
        >
          <Arrow
            {...arrowProps}
            size={10}
            roundness={0}
            style={{ ...arrowProps.style, filter: 'drop-shadow(0 rem(4) rem(10) red)' }}
          />
          <div className={'tooltip-content'} style={{ padding: `${remCss(8)}px ${remCss(10)}px` }}>
            {isReactText(text) ? lang.t(text) : text}
          </div>
        </div>
      )}
    </>
  );
};

const Tooltip = ({ children, markIcon, text, style, layerProp = {} }) => {
  const [isOver, hoverProps] = useHover({ delayEnter: 100, delayLeave: 300 });
  const [layerContainer, setLayerContainer] = useState(null);

  const { renderLayer, triggerProps, layerProps, arrowProps } = useLayer({
    triggerOffset: 23,
    ...layerProp,
    isOpen: isOver,
    container: layerContainer
  });

  let trigger;
  const iconname = markIcon === '?' ? BASE_ICONS.QUESTION_FILL : markIcon;
  if (iconname) {
    trigger = React.cloneElement(
      <div className={'mc-no-style-btn'} style={{ height: 20 }}>
        <SVGIcon
          fill={rgba(scssVariables['colors-mc-primary'], 0.8)}
          iconname={iconname}
          width={20}
        />
      </div>,
      {
        ...triggerProps,
        ...hoverProps
      }
    );
  } else {
    if (isReactText(children)) {
      trigger = (
        <span {...triggerProps} {...hoverProps}>
          {children}
        </span>
      );
    } else {
      // In case of an react-element, we need to clone it in order to attach our own props
      trigger = React.cloneElement(children, {
        ...triggerProps,
        ...hoverProps
      });
    }
  }

  let textChildren = isReactText(text) ? <MCText label={text} heading size={18} /> : text;

  return (
    <>
      {renderLayer(
        <div
          {...layerProps}
          style={{ ...layerProps.style, ...style }}
          className={'tooltip ' + (isOver ? 'show' : 'hide')}
        >
          <Arrow
            {...arrowProps}
            size={15}
            roundness={0}
            style={{ ...arrowProps.style, filter: 'drop-shadow(0 rem(4) rem(50) red)' }}
          />
          <div className={'tooltip-content'}>{textChildren}</div>
        </div>
      )}
      {trigger}
      <div className={'tooltip-layer'} ref={setLayerContainer}></div>
    </>
  );
};

export default Tooltip;
